/**
 * Generic listing enhancer for horizontal product/card rows.
 *
 * On any [data-marquee] element that contains a [data-marquee-track]:
 *   data-randomize="true"    — shuffle the items on load (show a random order)
 *   data-keep="N"            — trim down to N items (applied after shuffling)
 *   data-autoscroll="true"   — clone the items and auto-scroll horizontally
 *   data-pause-hover="true"  — pause the auto-scroll on hover/focus (default: true)
 *   data-speed="40"          — seconds for one full scroll loop
 *
 * Two autoscroll engines:
 *   Desktop — CSS keyframe animation on the track (adds .is-marquee).
 *     Skipped under prefers-reduced-motion (row stays a scrollable strip).
 *   Mobile/touch — a native overflow scroller advanced from rAF (adds
 *     .is-marquee-touch). CSS marquee animations proved unreliable on real
 *     phones (mask rasterization bugs, OS motion settings) and blocked
 *     manual swiping; a real scroller can always be swiped, and autoscroll
 *     pauses while a finger is down, resuming shortly after.
 *
 * [data-touch-marquee] (+ data-speed) opts a section whose track is already
 * duplicated in Liquid (e.g. the social feed) into the same touch engine;
 * its desktop CSS animation is left alone.
 */
(function () {
  var touchMode = window.matchMedia && window.matchMedia('(max-width: 989px), (hover: none)').matches;
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function shuffle(arr) {
    for (var i = arr.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var tmp = arr[i];
      arr[i] = arr[j];
      arr[j] = tmp;
    }
    return arr;
  }

  /* Advance a native scroller at a steady speed with a seamless wrap at the
     halfway point (the track holds two copies of the content). Pauses while
     the user is touching it and resumes shortly after they let go. */
  function touchLoop(container, track, speedSeconds) {
    var pos = null; /* float accumulator — scrollLeft rounds to ints on some engines */
    var half = 0;
    var pxPerSec = 0;
    var userHold = false;
    var resumeTimer = null;
    var lastT = null;

    function measure() {
      half = track.scrollWidth / 2;
      pxPerSec = half > 0 && speedSeconds > 0 ? half / speedSeconds : 0;
    }

    measure();
    if (window.ResizeObserver) {
      new ResizeObserver(measure).observe(track);
    } else {
      window.addEventListener('resize', measure);
    }

    function hold() {
      userHold = true;
      if (resumeTimer) clearTimeout(resumeTimer);
    }

    function release() {
      if (resumeTimer) clearTimeout(resumeTimer);
      resumeTimer = setTimeout(function () {
        pos = null; /* resync from wherever the swipe (and momentum) ended */
        userHold = false;
      }, 1500);
    }

    container.addEventListener('touchstart', hold, { passive: true });
    container.addEventListener('touchend', release, { passive: true });
    container.addEventListener('touchcancel', release, { passive: true });

    /* Wrap manual swipes too — jumping by one copy width is invisible */
    container.addEventListener('scroll', function () {
      if (!userHold || !half) return;
      if (container.scrollLeft >= half) container.scrollLeft -= half;
    }, { passive: true });

    function step(t) {
      if (lastT === null) lastT = t;
      var dt = Math.min((t - lastT) / 1000, 0.1);
      lastT = t;
      if (!userHold && pxPerSec) {
        if (pos === null) pos = container.scrollLeft;
        pos += pxPerSec * dt;
        if (half && pos >= half) pos -= half;
        container.scrollLeft = pos;
      }
      requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  function init(el) {
    if (el.__marqueeInit) return;
    el.__marqueeInit = true;

    var track = el.querySelector('[data-marquee-track]');
    if (!track) return;

    var randomize = el.getAttribute('data-randomize') === 'true';
    var autoscroll = el.getAttribute('data-autoscroll') === 'true';
    var pauseHover = el.getAttribute('data-pause-hover') !== 'false';
    var keep = parseInt(el.getAttribute('data-keep'), 10);
    var speed = parseFloat(el.getAttribute('data-speed')) || 40;

    var items = Array.prototype.slice.call(track.children);
    if (!items.length) return;

    /* Randomize order */
    if (randomize) {
      shuffle(items);
    }

    /* Trim to the desired count (e.g. a random subset of a larger pool) */
    if (!isNaN(keep) && keep > 0 && items.length > keep) {
      items.slice(keep).forEach(function (extra) {
        if (extra.parentNode) extra.parentNode.removeChild(extra);
      });
      items = items.slice(0, keep);
    }

    /* Re-append in shuffled order */
    if (randomize) {
      items.forEach(function (it) { track.appendChild(it); });
    }

    if (!autoscroll) return;

    /* Duplicate the set once for a seamless loop (both engines rely on it) */
    function cloneSet() {
      items.forEach(function (it) {
        var clone = it.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        var focusables = clone.querySelectorAll('a, button, input, select, textarea, [tabindex]');
        Array.prototype.forEach.call(focusables, function (f) { f.setAttribute('tabindex', '-1'); });
        track.appendChild(clone);
      });
    }

    if (touchMode) {
      cloneSet();
      el.classList.add('is-marquee-touch');
      touchLoop(el, track, speed);
    } else if (!reduceMotion) {
      cloneSet();
      track.style.setProperty('--marquee-duration', speed + 's');
      el.classList.add('is-marquee');
      if (pauseHover) el.classList.add('is-pause-hover');
    }
  }

  /* Sections whose track is already duplicated in Liquid */
  function initTouchOnly(el) {
    if (!touchMode || el.__marqueeInit) return;
    el.__marqueeInit = true;
    var track = el.querySelector('[data-touch-marquee-track]') || el.firstElementChild;
    if (!track) return;
    var speed = parseFloat(el.getAttribute('data-speed')) || 40;
    el.classList.add('is-marquee-touch');
    touchLoop(el, track, speed);
  }

  function initAll(root) {
    Array.prototype.forEach.call(root.querySelectorAll('[data-marquee]'), init);
    Array.prototype.forEach.call(root.querySelectorAll('[data-touch-marquee]'), initTouchOnly);
  }

  if (document.readyState !== 'loading') {
    initAll(document);
  } else {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  }

  /* Theme editor re-injects sections after DOMContentLoaded — without this
     a re-rendered marquee never gets its clones and sits static. */
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
})();
